import {useUiConfig} from '../../providers/UiConfigProvider';
import {useMediaQuery} from '../../providers/UiMediaQueryProvider';

/**
 * Used for feature flagging bottom sheets, can be removed once the rollout is finished.
 */
export type BottomSheetOverlayType = 'dialog' | 'drawer' | 'popover';

function resolveBottomSheetsEnabled(
  enableBottomSheets:
    | boolean
    | {
        dialog?: boolean;
        drawer?: boolean;
        popover?: boolean;
      }
    | undefined,
  overlayType: BottomSheetOverlayType,
): boolean {
  if (enableBottomSheets === undefined || enableBottomSheets === false) {
    return false;
  }
  if (enableBottomSheets === true) {
    return true;
  }
  return enableBottomSheets[overlayType] === true;
}

/**
 * Temporary hook used to adapt overlay styles to bottom sheets during the
 * bottom sheet feature flag rollout. Returns `true` only when the feature flag
 * is enabled for the given overlay type, and the device is a mobile touch
 * device on a small screen.
 *
 * If your styles work in both bottom sheets and the current
 * floating mobile overlays, use this instead:
 * ```tsx
 * css({
 *   [media.down(tokens.viewport.mobile)]: { ... }
 * })
 * ```
 *
 * Once the rollout is complete, this hook will be removed and its call sites
 * replaced with a simpler check.
 */
export function useShouldDisplayBottomSheet(
  overlayType: BottomSheetOverlayType,
) {
  const {isSmallScreen, isTouch} = useMediaQuery();
  const uiConfig = useUiConfig();
  const enabledBottomSheets = resolveBottomSheetsEnabled(
    uiConfig?.enableBottomSheets,
    overlayType,
  );

  return (isSmallScreen ?? false) && (isTouch ?? false) && enabledBottomSheets;
}
